import type {
  GrantKey,
  PermissionDraftMap,
  PermissionModule,
  PermissionGrantRow,
  PermissionHelperKind,
  PermissionMode,
  PermissionScope,
} from '@/types/permissionModules';

// Drawer catalog and grant pairing rules. This logic may move to helper functions
// on the permissions model later, once the permissions API is implemented and the
// payload shape is known.

function grant(
  key: GrantKey,
  mode: PermissionMode,
  scope: PermissionScope,
  editable = true,
): PermissionGrantRow {
  return {
    kind: 'grant',
    key,
    mode,
    scope,
    editable,
  };
}

const moduleTitleKey = (id: PermissionModule['id']) =>
  `settings.userPermissions.permissions.modules.${id}.title`;

/**
 * Granular Permissions Reference (TDD) as drawer modules.
 *
 * Area order matches primary nav after Full Access (Benchmarks → Agents → Rosters).
 * Accordion layout follows `UserPermissionPanelAccordion.vue` as a base; User Permissions Figma
 * overrides legacy chrome (no per-module description line or team count chip).
 */
export const PERMISSION_MODULES: PermissionModule[] = [
  {
    id: 'fullAccess',
    titleKey: moduleTitleKey('fullAccess'),
    rows: [
      {
        kind: 'helper',
        helper: 'selectAllView',
      },
      {
        kind: 'helper',
        helper: 'selectAllEdit',
      },
    ],
  },
  {
    id: 'budgets',
    titleKey: moduleTitleKey('budgets'),
    rows: [
      grant('editInstitutionalNil', 'edit', 'team'),
      grant('viewInstitutionalNil', 'view', 'team'),
      grant('editThirdPartyNil', 'edit', 'team'),
      grant('viewThirdPartyNil', 'view', 'team'),
      grant('editScholarships', 'edit', 'team'),
      grant('viewScholarships', 'view', 'team'),
    ],
  },
  {
    id: 'agreements',
    titleKey: moduleTitleKey('agreements'),
    rows: [
      grant('editRevShareAgreements', 'edit', 'team'),
      grant('viewRevShareAgreements', 'view', 'team'),
      grant('editScholarshipAgreements', 'edit', 'team'),
      grant('viewScholarshipAgreements', 'view', 'team'),
    ],
  },
  {
    id: 'payments',
    titleKey: moduleTitleKey('payments'),
    rows: [
      grant('editPayments', 'edit', 'team'),
      grant('viewPayments', 'view', 'team'),
    ],
  },
  {
    id: 'benchmarks',
    titleKey: moduleTitleKey('benchmarks'),
    rows: [grant('viewEarningsBenchmarks', 'view', 'org')],
  },
  {
    id: 'agents',
    titleKey: moduleTitleKey('agents'),
    rows: [
      grant('editAgents', 'edit', 'org'),
      grant('viewAgents', 'view', 'org'),
    ],
  },
  {
    id: 'rosters',
    titleKey: moduleTitleKey('rosters'),
    rows: [
      grant('editRoster', 'edit', 'team'),
      grant('viewRoster', 'view', 'team', false),
    ],
  },
];

const GRANT_ROWS: PermissionGrantRow[] = PERMISSION_MODULES.flatMap((permissionModule) =>
  permissionModule.rows.filter((row): row is PermissionGrantRow => row.kind === 'grant'),
);

/** All grant-backed rows across modules (stable list for helpers and tests). */
export function grantRows(): PermissionGrantRow[] {
  return GRANT_ROWS;
}

/** Toggleable grant rows controlled by a Full Access helper (shared with edit-drawer mutations). */
export function helperRows(helper: PermissionHelperKind): PermissionGrantRow[] {
  const mode: PermissionMode = helper === 'selectAllView' ? 'view' : 'edit';

  return GRANT_ROWS.filter((row) => row.editable && row.mode === mode);
}

/**
 * Every toggleable grant, as a Super Admin's effective permission set. Super Admins hold all
 * permissions by role and store none, so the edit drawer shows this set read-only. Roster VIEW is
 * derived and never stored.
 */
export function fullAccessDraft(): PermissionDraftMap {
  return Object.fromEntries(
    GRANT_ROWS.filter((row) => row.editable).map((row) => [row.key, true]),
  ) as PermissionDraftMap;
}

/**
 * True when every EDIT row is granted, i.e. the "Can edit all areas" helper is satisfied.
 * EDIT implies VIEW, so this also implies "Can view all areas" (including VIEW-only areas).
 */
export function isEditAllGranted(draft: PermissionDraftMap): boolean {
  const rows = helperRows('selectAllEdit');

  return rows.length > 0 && rows.every((row) => !!draft[row.key]);
}

/**
 * EDIT implies VIEW for stored pairs (TDD derived rule 8).
 * Roster VIEW is derived from any other grant (rule 9) and is not a stored pair.
 */
const PAIRED_VIEW_BY_EDIT = {
  editInstitutionalNil: 'viewInstitutionalNil',
  editThirdPartyNil: 'viewThirdPartyNil',
  editScholarships: 'viewScholarships',
  editRevShareAgreements: 'viewRevShareAgreements',
  editScholarshipAgreements: 'viewScholarshipAgreements',
  editPayments: 'viewPayments',
  editAgents: 'viewAgents',
} as const satisfies Partial<Record<GrantKey, GrantKey>>;

export function pairedViewKey(key: GrantKey): GrantKey | undefined {
  const pairs: Partial<Record<GrantKey, GrantKey>> = PAIRED_VIEW_BY_EDIT;

  return pairs[key];
}

/**
 * True when this VIEW key cannot be cleared: its paired EDIT is granted, or it is a VIEW-only
 * row already held while "Can edit all areas" is granted. EDIT keys are never locked.
 */
export function isViewLockedByEdit(draft: PermissionDraftMap, key: GrantKey): boolean {
  const isViewGrant = helperRows('selectAllView').some((row) => row.key === key);

  if (!isViewGrant) {
    return false;
  }

  const hasPairedEdit = Object.entries(PAIRED_VIEW_BY_EDIT)
    .some(([editKey, viewKey]) => viewKey === key && editKey in draft);

  if (hasPairedEdit) {
    return true;
  }

  // A VIEW-only row (no EDIT pair, e.g. Earnings Benchmarks) is only locked once it is held.
  // Leaving an absent VIEW-only row grantable stops edit-all from stranding it unchecked.
  return isEditAllGranted(draft) && !!draft[key];
}

/** Roster VIEW is held when the draft contains any grant other than Roster VIEW itself. */
export function isRosterViewHeld(draft: PermissionDraftMap): boolean {
  return Object.keys(draft).some((key) => key !== 'viewRoster' && key in draft);
}
